import { realtimeV1 } from '@udm/contracts';
import { useEffect, type ReactNode } from 'react';
import { getDataSource } from '../data';
import { clearCommands, onCommandEvent } from '../features/devices/commands';
import { trackPresence, useConnection } from './aggregates';
import { clearStatic, loadStatic } from './deviceStatic';
import { createEventRouter } from './eventRouter';
import { deviceLive } from './stores';
import { RealtimeClient } from './wsClient';

const route = createEventRouter({ devices: deviceLive, onCommandStatus: onCommandEvent });

async function bootstrapSnapshot(): Promise<string[]> {
  const ds = await getDataSource();
  const snap = await ds.liveSnapshot();
  loadStatic(snap.devices.map((d) => d.static));
  deviceLive.reconcile(snap.devices.map((d) => [d.static.id, d.live]));
  return snap.devices.map((d) => d.static.id);
}

/**
 * Snapshot + delta bootstrap (PROJECT_SPEC §58): load the snapshot once, then apply realtime deltas.
 * On resync only the snapshot is re-read and reconciled by state_version — never a page reload.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const stopTracking = trackPresence(deviceLive);
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const client = new RealtimeClient({
      url: `${scheme}://${location.host}${realtimeV1.REALTIME_PATH}`,
      clientVersion: __APP_VERSION__,
      onBatch: route,
      onResyncRequired: () => void bootstrapSnapshot().catch(() => undefined),
      onState: (state) => useConnection.setState({ state }),
    });
    client.start();
    // Everything the user may see (the server intersects it with the RBAC scope); map and lists filter locally.
    client.subscribe({ id: 'scope', topic: 'site' });
    const nudge = () => client.nudge();
    window.addEventListener('online', nudge);
    document.addEventListener('visibilitychange', nudge);

    let stopSim: (() => void) | undefined;
    let cancelled = false;
    void bootstrapSnapshot()
      .then(async (ids) => {
        // Static env check → the mock simulator is dead code (and no chunk) in production builds.
        if (import.meta.env.VITE_DATA_SOURCE === 'mock' && !cancelled) {
          const { startLiveSimulator } = await import('../mocks/liveSimulator');
          stopSim = startLiveSimulator(ids, (id) => deviceLive.get(id), route);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      stopSim?.();
      client.stop();
      stopTracking();
      window.removeEventListener('online', nudge);
      document.removeEventListener('visibilitychange', nudge);
      // Unmounted at sign-out (AuthGate): nothing of this session's devices may survive into the next one.
      deviceLive.clear();
      clearCommands();
      clearStatic();
    };
  }, []);
  return <>{children}</>;
}
